import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  PaginationModule,
} from 'ag-grid-community';
import 'ag-grid-enterprise';
import { SetFilterModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { logger } from '../../../services/logger/logger';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAgGridTheme } from '../../../hooks/useAgGridTheme';
import {
  closeAllFilterPopups as closeAllFilterPopupsImpl,
  getDynamicRowHeight as calcDynamicRowHeight,
  mapGridColumnDefs,
  getResponsiveHeaderHeight,
} from '../../../utils/agGridHelpers';
import {
  AgGridShell,
  useContainerResizeObserver,
  useFlexSizing,
  useGridPopupAutoClose,
  useRowHeightAutoUpdate,
} from './AgGridShell';
import { CustomTooltip } from './ClientSideAgGrid';
import CustomLoading from './CustomLoading';

ModuleRegistry.registerModules([PaginationModule, SetFilterModule]);

interface ServerSideAgGridProps {
  columnDefs: ColDef[];
  rowData?: any[];
  totalRecords?: number;
  isLoading?: boolean;
  isError?: boolean;
  onPageChange?: (_page: number) => void;
  onPageSizeChange?: (_pageSize: number) => void;
  onFilterChange?: (_filterModel: any) => void;
  onSortChange?: (_sortModel: any) => void;
  currentPage?: number;
  pageSize?: number;
  resetFiltersSignal?: number | string | boolean;
  initialFilterModel?: any; // Restore saved AG Grid filter model on mount
  domLayout?: 'normal' | 'autoHeight' | 'print';
}

const ServerSideAgGrid: React.FC<ServerSideAgGridProps> = ({
  columnDefs,
  rowData = [],
  totalRecords = 0,
  isLoading = false,
  isError: _isError = false,
  onPageChange,
  onPageSizeChange,
  onFilterChange,
  onSortChange,
  currentPage: externalCurrentPage = 1,
  pageSize: externalPageSize = 25,
  resetFiltersSignal,
  initialFilterModel,
  domLayout,
}) => {
  const muiTheme = useTheme();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [rowHeight, setRowHeight] = useState(50);
  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const initialFilterAppliedRef = useRef(false);

  // Restore saved filter model once gridApi is ready AND columnDefs have loaded
  // (columnDefs change when filter option queries resolve, populating set filter values)
  useEffect(() => {
    if (
      !gridApi ||
      !initialFilterModel ||
      initialFilterAppliedRef.current ||
      Object.keys(initialFilterModel).length === 0
    ) {
      return;
    }

    // Check if set filters have their values loaded by checking columnDefs filterParams
    const hasSetFilters = columnDefs.some((col) => col.filter === 'agSetColumnFilter');

    if (hasSetFilters) {
      // Small delay to let AG Grid process the new columnDefs & populate set filter values
      const timer = setTimeout(() => {
        try {
          gridApi.setFilterModel(initialFilterModel);
          initialFilterAppliedRef.current = true;
        } catch (e) {
          logger.error('Failed to restore filter model:', e as Error);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      // No set filters — apply immediately
      try {
        gridApi.setFilterModel(initialFilterModel);
        initialFilterAppliedRef.current = true;
      } catch (e) {
        logger.error('Failed to restore filter model:', e as Error);
      }
    }
  }, [gridApi, columnDefs, initialFilterModel]);

  const pageSize = externalPageSize;
  const currentPage = externalCurrentPage;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Dynamic row height based on container height and page size (shared helper)
  const getDynamicRowHeight = useCallback(() => {
    if (!containerRef.current) return 42; // fallback height
    const containerHeight = containerRef.current.clientHeight;
    const availableHeight = containerHeight - getResponsiveHeaderHeight();
    return calcDynamicRowHeight(availableHeight, pageSize);
  }, [pageSize]);

  const theme = useAgGridTheme({
    rowHeight,
    themeParams: {},
  });

  const closeAllFilterPopups = useCallback(
    () => closeAllFilterPopupsImpl(gridApi as GridApi),
    [gridApi],
  );

  useGridPopupAutoClose(containerRef, gridApi, closeAllFilterPopups);

  useContainerResizeObserver(containerRef, (cw) => {
    setContainerWidth(cw);
    setRowHeight(getDynamicRowHeight());
  });

  useRowHeightAutoUpdate(
    true,
    getDynamicRowHeight,
    `${pageSize}-${containerWidth ?? 'na'}`,
    setRowHeight,
  );

  // Handle grid ready
  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);

    // Calculate initial row height after grid is ready
    window.requestAnimationFrame(() => {
      const newRowHeight = getDynamicRowHeight();
      setRowHeight(newRowHeight);
    });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  // Handle filter and sort changes
  useEffect(() => {
    if (gridApi) {
      const onFilterChanged = () => {
        // Always propagate filter changes to the parent so that
        // the first user interaction also triggers an API call.
        const filterModel = gridApi.getFilterModel();
        if (onFilterChange) {
          onFilterChange(filterModel);
        }
      };

      const onSortChanged = () => {
        const sortModel = (gridApi as any).getColumnState();
        const sort = sortModel.filter((item: any) => item.sort !== null);
        if (onSortChange) {
          onSortChange(sort);
        }
      };

      gridApi.addEventListener('filterChanged', onFilterChanged);
      gridApi.addEventListener('sortChanged', onSortChanged);

      return () => {
        gridApi.removeEventListener('filterChanged', onFilterChanged);
        gridApi.removeEventListener('sortChanged', onSortChanged);
      };
    }
  }, [gridApi, onSortChange, onFilterChange]);

  useEffect(() => {
    if (!gridApi) return;

    try {
      const model = gridApi.getFilterModel?.();
      const hasFilters = !!(model && Object.keys(model).length > 0);
      if (hasFilters) {
        gridApi.setFilterModel(null);
        (gridApi as any).onFilterChanged?.();
      }
    } catch (error) {
      logger.error('Failed to reset filters:', error as Error);
    }
  }, [resetFiltersSignal, gridApi]);

  // Set updating flag when data changes
  useEffect(() => {
    if (rowData && rowData.length > 0) {
      setIsUpdatingData(true);
      const timer = setTimeout(() => {
        setIsUpdatingData(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [rowData]);

  const { applyFlexSizing } = useFlexSizing(columnDefs || [], containerWidth);

  return (
    <AgGridShell
      gridHeight="calc(100% - 40px)"
      containerStyle={{
        backgroundColor: muiTheme.palette.common.white,
        borderRadius: `${muiTheme.borderRadius.xl}px`,
        boxShadow: muiTheme.palette.shadow.cardContent,
      }}
      containerRef={containerRef}
      showPagination={true}
      paginationProps={{
        page: currentPage - 1,
        pageCount: totalPages,
        pageSize,
        onPageChange: ({ page, pageSize: newPageSize }) => {
          if (newPageSize !== pageSize) {
            handlePageSizeChange(newPageSize);
          }
          handlePageChange(page + 1);
        },
        onPageSizeChange: ({ pageSize: newPageSize }) => {
          handlePageSizeChange(newPageSize);
        },
        totalRecords,
      }}
    >
      <AgGridReact
        columnDefs={mapGridColumnDefs(columnDefs || [], {
          applyFlexSizing,
          tooltipComponent: CustomTooltip,
          containerHeight: containerRef.current?.clientHeight || 300,
          defaultWidth: 120,
          getTooltipWidth: ({ width, rest, defaultWidth }) =>
            (width as number) || ((rest as any)?.minWidth as number) || defaultWidth,
        })}
        rowData={rowData}
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[10, 25, 50, 100]}
        animateRows
        theme={theme}
        onGridReady={onGridReady}
        loading={isLoading}
        loadingOverlayComponent={() => <CustomLoading />}
        suppressSetFilterByDefault={isUpdatingData}
        tooltipShowDelay={300}
        tooltipHideDelay={5000}
        popupParent={document.body}
        context={{
          containerHeight: containerRef.current?.clientHeight || 300,
        }}
        domLayout={domLayout}
      />
    </AgGridShell>
  );
};

export default ServerSideAgGrid;
